import type { ParametersAbstractParameter } from "@/lib/api";
import { toTimeSeriesParameterOption } from "@/features/shared/widgets/map/time-series/time-series-parameter-option.utils";
import type { TimeSeriesParameterOption } from "@/features/shared/widgets/map/time-series/time-series-parameter.types";

export type TimeSeriesCatalogueParameter = Pick<
  ParametersAbstractParameter,
  "id" | "label" | "units" | "property_kind_id"
>;

export type TimeSeriesCatalogueModel = {
  id: string;
  name: string;
};

export type TimeSeriesCatalogueLabels = {
  observed: string;
  model: (name: string) => string;
};

/**
 * Flattens observed parameters plus every model's parameter list into picker
 * options. IDs are unique across datasets; `datasetLabel` is the localized
 * source (`observed` or `model: XYZ`).
 */
export function mapAllTimeSeriesParameters(
  observed: readonly TimeSeriesCatalogueParameter[],
  models: readonly TimeSeriesCatalogueModel[],
  parametersByModelId: Readonly<Record<string, readonly TimeSeriesCatalogueParameter[]>>,
  labels: TimeSeriesCatalogueLabels,
): TimeSeriesParameterOption[] {
  const options: TimeSeriesParameterOption[] = observed.map((parameter) => ({
    ...toTimeSeriesParameterOption(parameter),
    datasetLabel: labels.observed,
  }));

  for (const model of models) {
    const parameters = parametersByModelId[model.id];
    if (!parameters) continue;
    const datasetLabel = labels.model(model.name);
    for (const parameter of parameters) {
      options.push({
        ...toTimeSeriesParameterOption(parameter),
        datasetLabel,
      });
    }
  }

  return options;
}

/** Observed plus every model, including models that currently have no parameters. */
export function listTimeSeriesDatasetLabels(
  models: readonly TimeSeriesCatalogueModel[],
  labels: TimeSeriesCatalogueLabels,
): string[] {
  return [labels.observed, ...models.map((model) => labels.model(model.name))];
}
